import { useMemo } from 'react';
import { chairStaff, useStaff } from '../../data/salon.js';
import { useAuth } from '../../lib/auth.js';
import { ChairRentPanel } from './ChairRentPanel.js';
import { CommsCostCard } from './CommsCostCard.js';
import { EarningsSection } from './EarningsSection.js';
import { FutureRevenueCard } from './FutureRevenueCard.js';
import { OccupancyCard } from './OccupancyCard.js';
import { PaymentCostCompositionCard } from './PaymentCostCompositionCard.js';
import { StaffKpiCard } from './StaffKpiCard.js';
import { TakingsCard } from './TakingsCard.js';
import { DateRange, useDateRange, useFutureHorizon } from './shared.js';

/**
 * WS9 — the "Reports" tab. Composes the SIX server-computed reports + the corrected future-revenue projection
 * (the HERO, replacing the old mis-sourced client-side estimate) and the per-staff KPI dashboard (replacing
 * the old client-side per-chair cards). Every figure is the server's (ADR-0009 money honesty — nothing is
 * recomputed in the browser); each card carries loading/empty/error states + a CSV export of the server rows.
 *
 * Two TIME controls, because the reports look in OPPOSITE directions: the HERO future-revenue projection is
 * FORWARD-looking (its own useFutureHorizon — anchored to today + a forward horizon), while the other five are
 * HISTORICAL (the shared last-30-days DateRange). Reusing the backward window for the projection pointed the
 * "future" hero at the PAST — the WS-9 defect this fixes. The two MANAGER-ONLY API reports (comms-cost,
 * payment-cost-composition) gate their read on isFloorManager via the `enabled` arg, so a non-manager never
 * fires a 403-guaranteed request and instead sees an honest note. The already-surfaced earnings + chair-rent
 * panels are re-mounted here so nothing regresses; chair-rent recording is itself manager-only.
 */
export function ReportsTab() {
  const { isFloorManager } = useAuth();
  const { data: staff } = useStaff();
  const dateRange = useDateRange();
  const futureHorizon = useFutureHorizon();
  const { range } = dateRange;

  // Chair rent is recorded against renters' sealed books (the panel is manager-only on the API).
  const renters = useMemo(() => chairStaff(staff).filter((s) => s.renterId != null), [staff]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <FutureRevenueCard control={futureHorizon} />

      <DateRange control={dateRange} label="Reporting period" />

      <TakingsCard range={range} />
      <StaffKpiCard range={range} />
      <OccupancyCard range={range} />
      <CommsCostCard range={range} enabled={isFloorManager} />
      <PaymentCostCompositionCard range={range} enabled={isFloorManager} />

      <EarningsSection range={range} />
      {isFloorManager ? <ChairRentPanel renters={renters} range={range} /> : null}
    </div>
  );
}
